import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  CircularProgress,
  IconButton,
  Fab,
  Badge,
  Tooltip,
  alpha,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Refresh,
  Edit,
  CheckCircle,
  Cancel,
  FilterList,
  FilterAlt,
  Close,
  PictureAsPdf,
  Description as ExcelIcon,
  Assessment as CsvIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import axios from "../../../../utils/axios";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import useWindowDimensions from "../../../../utils/windowDimensions";
import useMainContentDimensions from "../../../../utils/mainContentDimensions";
import EditDelete from "../../../../components/EditDelete";
import useInventory from "../../../../hooks/useInventory";
import { saveFile } from "../../../../utils/common";
import ToolBar from "../../../../components/Toolbar";

function MaterialCategoryList() {
  const dispatch = useDispatch();
  const { exportInventoryReport } = useInventory();
  const { height } = useWindowDimensions();
  const { width: mainContentWidth } = useMainContentDimensions();

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  const handleDownload = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const resBlob = await exportInventoryReport("material-category", fileType, { filters: [] });
      await saveFile(
        resBlob,
        `material_category_${new Date().toISOString().slice(0, 10)}.${fileType}`,
      );
      dispatch(showSuccessSnackbar(`Exported as ${fileType.toUpperCase()} successfully!`));
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to export"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempSearchCode, setTempSearchCode] = useState("");
  const [tempSearchName, setTempSearchName] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Dialog state
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [categoryCode, setCategoryCode] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [formSubmitting, setFormSubmitting] = useState(false);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const filters = [];
      if (searchName) {
        filters.push({
          fieldName: "categoryName",
          fieldValue: searchName,
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (searchCode) {
        filters.push({
          fieldName: "categoryCode",
          fieldValue: searchCode,
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      const response = await axios.post("/material-categories/list", {
        pagination: {
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
        },
        filters,
        sortFields: [
          {
            field: "categoryName",
            sort: "ASC",
          },
        ],
      });
      if (response && response.data) {
        setCategories(response.data.data || []);
        setTotalCount(response.data.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
      dispatch(showErrorSnackbar("Failed to fetch categories"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [paginationModel, searchName, searchCode]);

  const handleRefresh = () => {
    setSearchName("");
    setSearchCode("");
    setTempSearchName("");
    setTempSearchCode("");
    setPaginationModel({ page: 0, pageSize: 25 });
  };

  const handleApplyFilter = () => {
    setSearchName(tempSearchName);
    setSearchCode(tempSearchCode);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    setTempSearchName("");
    setTempSearchCode("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const activeFiltersCount = (searchName ? 1 : 0) + (searchCode ? 1 : 0);

  const handleOpenAdd = () => {
    setIsEditing(false);
    setCategoryCode("");
    setCategoryName("");
    setStatus("ACTIVE");
    setIsOpen(true);
  };

  const handleOpenEdit = (row) => {
    setIsEditing(true);
    setCurrentId(row.categoryId);
    setCategoryCode(row.categoryCode);
    setCategoryName(row.categoryName);
    setStatus(row.status);
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      const payload = {
        categoryCode,
        categoryName,
        status,
      };

      if (isEditing) {
        await axios.put(`/material-categories/update/${currentId}`, payload);
        dispatch(showSuccessSnackbar("Category updated successfully"));
      } else {
        await axios.post("/material-categories/add", payload);
        dispatch(showSuccessSnackbar("Category created successfully"));
      }
      setIsOpen(false);
      fetchCategories();
    } catch (error) {
      console.error(error);
      const errMsg = error.response?.data?.message || "Failed to save category";
      dispatch(showErrorSnackbar(errMsg));
    } finally {
      setFormSubmitting(false);
    }
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 150,
      renderCell: (params) => (
        <EditDelete onEdit={() => handleOpenEdit(params.row)} showDelete={false} />
      ),
    },
    {
      field: "categoryCode",
      headerName: "Category Code",
      width: 200,
    },
    {
      field: "categoryName",
      headerName: "Category Name",
      width: 250,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: params.value === "ACTIVE" ? "green" : "red",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 220,
      valueGetter: (value) => (value ? new Date(value).toLocaleString() : ""),
    },
  ];

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        title="Material Category List"
        action={
          <Box display="flex" gap={1} alignItems="center" mt={1} mr={1}>
            <ToolBar
              addPage={handleOpenAdd}
              refreshPage={handleRefresh}
              openFilterDialog={() => setIsFilterOpen(true)}
              downloadFile={handleDownload}
              isLoadingDownload={isDownloading}
              downloadFileType={downloadType}
            />
          </Box>
        }
      />
      <CardContent>
        {/* Advance Filter Dialog */}
        <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="h6">Advance Filter</Typography>
            <IconButton onClick={() => setIsFilterOpen(false)} size="small">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 3 }}>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Code"
                  value={tempSearchCode}
                  onChange={(e) => setTempSearchCode(e.target.value)}
                  placeholder="Search category code"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Name"
                  value={tempSearchName}
                  onChange={(e) => setTempSearchName(e.target.value)}
                  placeholder="Search category name"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleResetFilter} variant="outlined" startIcon={<Refresh />}>
              Reset
            </Button>
            <Button
              onClick={handleApplyFilter}
              variant="contained"
              color="primary"
              startIcon={<FilterAlt />}
            >
              filter
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ height: height - 260, display: "flex", flexDirection: "column" }}>
          <DataGrid
            loading={isLoading}
            rows={categories}
            rowCount={totalCount}
            columns={columns}
            getRowId={(row) => row.categoryId}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9",
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff",
              },
            }}
          />
        </Box>
      </CardContent>

      {/* Add / Edit Category Dialog */}
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isEditing ? "Edit Category" : "Add Category"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Code"
                  value={categoryCode}
                  onChange={(e) => setCategoryCode(e.target.value)}
                  disabled={isEditing}
                  required
                  placeholder="e.g. ELEC"
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                  placeholder="e.g. Electronics"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formSubmitting}
              startIcon={formSubmitting ? <CircularProgress size={20} /> : null}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
}

export default MaterialCategoryList;
